import { supabase } from '@/config/supabase'
import type { Associado } from '@/types/database'

export interface ListFilters {
    search?: string
    status?: string
    asaas_empresa?: string
    page?: number
    pageSize?: number
}

export interface PaginatedResult<T> {
    data: T[]
    total: number
    page: number
    pageSize: number
    totalPages: number
}

export const associadoService = {
    async list(filters: ListFilters = {}): Promise<PaginatedResult<Associado>> {
        const { search, status, page = 1, pageSize = 10 } = filters

        // Supabase query
        let query = supabase
            .schema('veicular_system')
            .from('veic_associados')
            .select('*', { count: 'exact' })
            .is('deleted_at', null)
            .order('created_at', { ascending: false })

        if (search) {
            query = query.or(`nome.ilike.%${search}%,cpf.ilike.%${search}%,email.ilike.%${search}%`)
        }
        if (status && status !== 'todos') {
            query = query.eq('status', status)
        }
        if (filters.asaas_empresa === 'sem') {
            query = (query as any).is('asaas_customer_id', null)
        } else if (filters.asaas_empresa) {
            query = (query as any).eq('asaas_empresa', filters.asaas_empresa)
        }

        const from = (page - 1) * pageSize
        query = query.range(from, from + pageSize - 1)

        const { data, count, error } = await query
        if (error) throw error

        return {
            data: (data || []) as Associado[],
            total: count || 0,
            page,
            pageSize,
            totalPages: Math.ceil((count || 0) / pageSize),
        }
    },

    async getStatusCounts(): Promise<{ ativo: number; inadimplente: number; cancelado: number }> {
        const [a, i, c] = await Promise.all([
            supabase.schema('veicular_system').from('veic_associados').select('id', { count: 'exact', head: true }).eq('status', 'ativo').is('deleted_at', null),
            supabase.schema('veicular_system').from('veic_associados').select('id', { count: 'exact', head: true }).eq('status', 'inadimplente').is('deleted_at', null),
            supabase.schema('veicular_system').from('veic_associados').select('id', { count: 'exact', head: true }).eq('status', 'cancelado').is('deleted_at', null),
        ])
        return { ativo: a.count || 0, inadimplente: i.count || 0, cancelado: c.count || 0 }
    },

    async getById(id: string): Promise<Associado | null> {
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_associados')
            .select('*')
            .eq('id', id)
            .is('deleted_at', null)
            .single()
        if (error) throw error
        return data as Associado
    },

    // Public checkout: check if CPF is already registered (no auth required)
    async lookupByCpf(cpf: string): Promise<{ exists: boolean; associado?: Associado }> {
        const cleanCpf = cpf.replace(/\D/g, '')
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_associados')
            .select('*')
            .eq('cpf', cleanCpf)
            .is('deleted_at', null)
            .maybeSingle()
        if (error) return { exists: false }
        return { exists: !!data, associado: data as Associado | undefined }
    },

    async listByCpf(cpf: string): Promise<Associado[]> {
        const cleanCpf = cpf.replace(/\D/g, '')
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_associados')
            .select('*')
            .eq('cpf', cleanCpf)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
        if (error) throw error
        return (data || []) as Associado[]
    },

    async create(input: Partial<Associado>): Promise<Associado> {
        // @ts-ignore
        const { data, error } = await supabase.schema('veicular_system').from('veic_associados').insert(input as any).select().single()
        if (error) throw error
        return data as Associado
    },

    async update(id: string, input: Partial<Associado>): Promise<Associado> {
        // @ts-ignore
        const { data, error } = await supabase.schema('veicular_system').from('veic_associados').update(input as any).eq('id', id).select().single()
        if (error) throw error
        return data as Associado
    },

    async remove(id: string): Promise<void> {
        // @ts-ignore
        const { error } = await supabase.schema('veicular_system').from('veic_associados').update({ deleted_at: new Date().toISOString() } as any).eq('id', id)
        if (error) throw error
    },
}
